/**
 * useOnboardingStatus — live "is this org set up?" check, computed client-side
 * from where each Settings tab actually stores its data (no migration needed).
 *
 * Drives the persistent SetupBanner and gates the compliance orb: a new org sees
 * a "finish setup" nudge and no orb until the essentials are filled. We gate on
 * the SERVER-backed essentials (Business details + Tax & Compliance) — never on
 * the localStorage-only tabs (Invoice/Preferences), so an existing, set-up org
 * is never falsely locked just because it opened on a fresh browser. Invoice +
 * Preferences are surfaced as recommended steps, not blockers.
 *
 * Shared across the app (fastphone-2); recomputes on the live tick (which
 * includes window focus) and on the 'karr:onboarding-refresh'
 * event (dispatched by Settings on save) so the banner clears the moment setup
 * is done — without a reload.
 */
import { supabase } from '@/db/supabase';
import { createSharedRead } from '@/shared/lib/sharedRead';
import { useAuth } from './useAuth';
import { useLiveStore } from './useLiveRefresh';

export interface OnboardingStep {
    key: 'org' | 'tax' | 'opening' | 'invoice' | 'preferences';
    label: string;
    done: boolean;
    /** true = blocks the orb / counts toward "complete"; false = recommended only */
    required: boolean;
    href: string;
}

export interface OnboardingStatus {
    loading: boolean;
    /** All REQUIRED steps done (gates the orb + hides the banner). */
    complete: boolean;
    /** 0–100 across all (required + recommended) steps, for the progress bar. */
    percent: number;
    /** Count of not-yet-done steps (required + recommended). */
    remaining: number;
    steps: OnboardingStep[];
}

const EMPTY: OnboardingStatus = { loading: true, complete: true, percent: 100, remaining: 0, steps: [] };
const NO_ORG: OnboardingStatus = { ...EMPTY, loading: false };

async function computeOnboarding(orgId: string): Promise<OnboardingStatus> {
    const { data: org } = await supabase
        .from('orgs')
        .select('name, country, org_type, address_line1, city, pincode, org_config')
        .eq('id', orgId)
        .maybeSingle();

    const cfg = (org?.org_config ?? {}) as Record<string, unknown>;

    // Invoice + Preferences live in localStorage (per-browser) — recommended only.
    let invoiceDone = false;
    let prefsDone = false;
    try {
        const raw = localStorage.getItem(`karr_settings_${orgId}`);
        if (raw) {
            const p = JSON.parse(raw) as { invoice?: { invoice_prefix?: string }; prefs?: { currency?: string } };
            invoiceDone = !!p?.invoice?.invoice_prefix;
            prefsDone = !!p?.prefs?.currency;
        }
    } catch { /* ignore */ }

    const orgDone = !!(org?.name && org?.country && org?.org_type && org?.address_line1 && org?.city && org?.pincode);
    const taxDone = !!((cfg.primary_tax_id as string) || (cfg.gstin as string));

    // Opening balances are recommended for businesses migrating INTO Karr.
    // Any posted transaction satisfies this — either they entered opening
    // balances (which post as transactions) or they've already started
    // trading, in which case the "starting balances" nudge is moot. This
    // keeps us from nagging brand-new, from-zero businesses forever.
    const { count: txnCount } = await supabase
        .from('transactions')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', orgId);
    // Done if the org has posted any transaction (openings post as txns, or
    // they've started trading) OR the user consciously confirmed there are
    // none / they're recorded (org_config.opening_confirmed). Soft-gate only.
    const openingDone = (txnCount ?? 0) > 0 || cfg.opening_confirmed === true;

    const steps: OnboardingStep[] = [
        { key: 'org', label: 'Business details', done: orgDone, required: true, href: '/settings' },
        { key: 'tax', label: 'Tax & compliance', done: taxDone, required: true, href: '/settings' },
        { key: 'opening', label: 'Starting balances', done: openingDone, required: false, href: '/opening-balances' },
        { key: 'invoice', label: 'Invoice defaults', done: invoiceDone, required: false, href: '/settings' },
        { key: 'preferences', label: 'Preferences', done: prefsDone, required: false, href: '/settings' },
    ];

    const requiredDone = steps.filter((s) => s.required).every((s) => s.done);
    const doneCount = steps.filter((s) => s.done).length;

    return {
        loading: false,
        complete: requiredDone,
        percent: Math.round((doneCount / steps.length) * 100),
        remaining: steps.filter((s) => !s.done).length,
        steps,
    };
}

// fastphone-2: one status per user and business for the whole app, refreshed by
// its event and the live tick (which includes tab focus), not by a mount.
const onboardingRead = createSharedRead<OnboardingStatus>((key) => computeOnboarding(key.split('|')[0]));
if (typeof window !== 'undefined') {
    const onRefresh = () => onboardingRead.refresh();
    window.addEventListener('karr:onboarding-refresh', onRefresh);
    // A test that mocks the live module without the store simply goes without.
    try { useLiveStore.subscribe(onRefresh); } catch { /* no live tick */ }
}

export function useOnboardingStatus(orgId?: string): OnboardingStatus {
    const userId = useAuth((s) => s.user?.id) ?? '';
    const read = onboardingRead.use(orgId ? `${orgId}|${userId}` : null);
    if (!orgId) return NO_ORG;
    return read.value ?? EMPTY;
}
